import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, fetchUserAttributes, signOut } from 'aws-amplify/auth';
import { useErrorHandler } from '@lib/useErrorHandler';

import { parseElemezHostname } from '../../lib/url';
import { getConfig } from '../../services/config/config';
import { Authenticator } from './authenticator';
import { AppLoading } from '../loading/appLoading';
import { AuthProvider } from '../../context/auth';
import { AUTHENTICATOR_AUTHSTATE, handleExternalAuthEvent, checkUser, AuthState, AUTH_CHANNEL, AuthStateType } from './utils';
import { UserWrapper } from '../../containers/app/userWrapper';

export const authHandlers = {
  handleAuthStateChange: async (
    payload: any,
    setUser: (user: any) => void,
    setAuthState: (state: AuthStateType) => void,
    setUserChecked: (checked: boolean) => void
  ) => {
    if (payload.event === 'authStateChange') {
      const nextAuthState = payload.message as AuthStateType;
      const authState: AuthStateType = nextAuthState === AuthState.SignedOut ? AuthState.SignIn : nextAuthState;
      let authData = payload.data;

      window.localStorage.setItem(AUTHENTICATOR_AUTHSTATE, authState);
      setUser(authData);
      setAuthState(authState);
      setUserChecked(true); // if authState change is triggered, user check is no longer required or was skipped via oauth

      // If user is signing in, fetch both user and attributes centrally
      if (authState === AuthState.SignedIn) {
        try {
          const currentUser = await getCurrentUser();
          const userAttributes = await fetchUserAttributes();
          setUser({ ...currentUser, userAttributes });
        } catch (error) {
          console.warn('Failed to fetch user or attributes, signing user out', error);
          // If we can't get user data, sign the user out
          try {
            await signOut();
          } catch (signOutError) {
            console.warn('Failed to sign out after user fetch error', signOutError);
          }
          // Set to SignIn state instead
          window.localStorage.setItem(AUTHENTICATOR_AUTHSTATE, AuthState.SignIn);
          setUser(undefined);
          setAuthState(AuthState.SignIn);
        }
      }
    }
  },

  configureAuth: async (
    authConfig: any,
    setAuthConfig: (config: any) => void,
    setError: (err: any) => void
  ) => {
    if (!authConfig) {
      try {
        const [brand] = parseElemezHostname();
        const url = `${window.location.origin}${window.location.pathname}`;
        const config = await getConfig(brand, url);
        const oauth = config.userAuth.oauth;

        if (oauth) {
          if (oauth.scope && !oauth.scopes) {
            oauth.scopes = oauth.scope;
            delete oauth.scope;
          }

          if (!Array.isArray(oauth.redirectSignIn)) {
            oauth.redirectSignIn = [oauth.redirectSignIn];
          }

          if (!Array.isArray(oauth.redirectSignOut)) {
            oauth.redirectSignOut = [oauth.redirectSignOut];
          }
        }

        const amplifyConfig = {
          Cognito: {
            userPoolClientId: config.userAuth.userPoolWebClientId,
            userPoolId: config.userAuth.userPoolId,
            userPoolEndpoint: config.userAuth.userPoolEndpoint,
            loginWith: oauth ? { oauth } : {}
          }
        };
        Amplify.configure({ Auth: amplifyConfig });
        setAuthConfig(config);
      } catch (err) {
        setError(err);
      }
    }
  },

  checkUserAuth: async (
    authConfig: any,
    setUserChecked: (checked: boolean) => void
  ) => {
    if (authConfig) {
      await checkUser(setUserChecked);
    }
  },

  selectApp: (authState: AuthStateType) => {
    return authState === AuthState.SignedIn ? <UserWrapper /> : <Authenticator />;
  }
};

export const AuthWrapper = () => {
  const [error, setError] = useState<Error>();
  const [authConfig, setAuthConfig] = useState<any>();
  const [authState, setAuthState] = useState<AuthStateType>(AuthState.SignIn);
  const [user, setUser] = useState<any>();
  // we use userChecked to ensure that we do not render anything until we have checked for an authenticated user
  const [userChecked, setUserChecked] = useState(false);

  useEffect(() => {
    // listen to messages about authState and user changes from other components, removes listener on un-mount
    const unsubscribe = Hub.listen('ui-auth', (data) => {
      authHandlers.handleAuthStateChange(data.payload, setUser, setAuthState, setUserChecked);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    // listen to messages from Amplify core
    const unsubscribe = Hub.listen(AUTH_CHANNEL, handleExternalAuthEvent);
    return unsubscribe;
  }, []);

  useEffect(() => {
    authHandlers.configureAuth(authConfig, setAuthConfig, setError);
  }, [authConfig]);

  useEffect(() => {
    authHandlers.checkUserAuth(authConfig, setUserChecked);
  }, [authConfig]);

  // rethrow any error after useEffect calls
  useErrorHandler(error);

  const userDataMissing = authState === AuthState.SignedIn && !user;
  if (!authConfig || !userChecked || userDataMissing) {
    return <AppLoading />;
  }

  const app = authHandlers.selectApp(authState);

  return (
    <AuthProvider value={{ authConfig, authState, user }} >
      {app}
    </AuthProvider>
  );
};
